import { Link } from "react-router-dom";
import { useState } from "react";
import { Mail, MapPin, Phone, Flame, ArrowUpRight, Facebook, Instagram } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ORG, NAV_LINKS, SOCIAL } from "@/lib/constants";
import Logo from "@/components/Logo";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list. Welcome to the Phoenix.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative mt-32 border-t border-white/5 bg-[#0A0A0A]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-4">
            <Logo size="lg" />
            <div className="overline">Est. 1967 · Detroit</div>
          </div>
          <h2 className="font-display text-5xl lg:text-7xl leading-[0.95] tracking-tight">
            Phoenix
            <span className="block text-amber-500">Rising.</span>
          </h2>
          <p className="mt-6 max-w-md text-slate-400 leading-relaxed">
            Six decades of brotherhood, sisterhood, advocacy, and service —
            a voice for minorities in the Detroit Fire Department and a steward of
            Historic Engine 11.
          </p>

          {/* Social links — only render when real URLs are configured */}
          <div className="mt-6 flex items-center gap-3 flex-wrap" data-testid="footer-social">
            {SOCIAL.facebookUrl ? (
              <a
                href={SOCIAL.facebookUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={SOCIAL.facebookLabel}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 text-slate-300 hover:text-amber-400 hover:border-amber-500/50 transition-colors text-xs"
                data-testid="social-facebook"
              >
                <Facebook className="h-3.5 w-3.5" />
                <span>Facebook</span>
              </a>
            ) : (
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 text-[10px] tracking-[0.22em] uppercase font-accent text-slate-400"
                data-testid="social-facebook-label"
                title={SOCIAL.facebookLabel}
              >
                <Facebook className="h-3.5 w-3.5 text-amber-500" />
                Facebook · {SOCIAL.facebookLabel}
              </span>
            )}
            {SOCIAL.instagram && (
              <a
                href={SOCIAL.instagram}
                target="_blank"
                rel="noreferrer"
                aria-label={`Phoenix on Instagram — ${SOCIAL.instagramHandle}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 text-slate-300 hover:text-amber-400 hover:border-amber-500/50 transition-colors text-xs"
                data-testid="social-instagram"
              >
                <Instagram className="h-3.5 w-3.5" />
                <span>{SOCIAL.instagramHandle}</span>
              </a>
            )}
          </div>

          <form onSubmit={subscribe} className="mt-8 flex flex-col sm:flex-row gap-2 max-w-md" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Join the newsletter"
              className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/50"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="btn-gold disabled:opacity-60 w-full sm:w-auto justify-center"
              data-testid="newsletter-submit-btn"
            >
              {loading ? "..." : "Subscribe"}
            </button>
          </form>
        </div>

        <div className="lg:col-span-4 grid grid-cols-2 gap-6">
          <div>
            <div className="overline mb-4">Explore</div>
            <ul className="space-y-2 text-sm">
              {NAV_LINKS.slice(0, 6).map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-slate-300 hover:text-amber-400 transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline mb-4">More</div>
            <ul className="space-y-2 text-sm">
              {NAV_LINKS.slice(6).map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-slate-300 hover:text-amber-400 transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="overline mb-4">Headquarters</div>
          <ul className="space-y-3 text-sm text-slate-300">
            <li className="flex gap-3"><MapPin className="h-4 w-4 mt-0.5 text-amber-500" /> {ORG.address}</li>
            <li className="flex gap-3"><Mail className="h-4 w-4 mt-0.5 text-amber-500" /> {ORG.emailConfirmed ? ORG.email : "Email coming soon"}</li>
            <li className="flex gap-3"><Phone className="h-4 w-4 mt-0.5 text-amber-500" /> {ORG.phone}</li>
          </ul>
          <Link to="/donate-sponsor" className="btn-primary mt-6" data-testid="footer-donate-btn">
            Support Our Mission <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <Flame className="h-3.5 w-3.5 text-amber-500" />
            <span>© {new Date().getFullYear()} {ORG.name}. 501(c)(3) Nonprofit.</span>
          </div>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link
              to="/privacy"
              className="text-slate-400 hover:text-amber-400 transition-colors"
              data-testid="footer-privacy-link"
            >
              Privacy
            </Link>
            <span className="text-slate-700">·</span>
            <Link
              to="/terms"
              className="text-slate-400 hover:text-amber-400 transition-colors"
              data-testid="footer-terms-link"
            >
              Terms
            </Link>
            <span className="text-slate-700 hidden md:inline">·</span>
            <span className="tracking-[0.28em] uppercase font-accent">Detroit · Est. 1967</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
